import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, TextField, Select, MenuItem, FormControl, InputLabel, IconButton, Autocomplete, Box, CircularProgress, Alert, Chip, Tooltip, Typography, Stepper, Step, StepLabel, RadioGroup, Radio, FormControlLabel, FormHelperText, Tabs, Tab, Checkbox, Grid, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import Datagrid from "../../components/Datagrid/Datagrid";
import DatagridLoading from "../../components/DatagridLoading/DatagridLoading";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { useSite } from "../../context/siteContext";
import { useAuth } from "../../context/authContext";
import Swal from "sweetalert2";
import { AddCircle } from "@mui/icons-material";
import { ALERT_MESSAGES, BUTTON, LABELS, MENU_ITEMS, TABLE_HEADERS, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { getDocumentCategories, getDocumentsMetaData, uploadDocument, updateDocument, deleteDocument, searchMachines as searchMachinesApi, getDocumentAudience, createDocumentCategory, updateDocumentCategory, deleteDocumentCategory, updateCategoryDocuments } from "../../services/documentService";
import { getMachinesByIds } from "../../services/breadCrumbsService";
import { getUserAllocations } from "../../services/manageSitesService";
import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import RichTextEditor from "../../components/RichTextEditor/RichTextEditor";
import FileDropZone from "../../components/FileDropZone/FileDropZone";
import { SERVER_BASE_URL } from "../../config";
import DocCard from "../../components/DocCard/DocCard";
import { CategoryViewModal, AddCategoryModal, EditCategoryModal, DeleteCategoryModal } from "../../components/DocumentCategoryModals";
import NotAuthorized from "../../components/NotAuthorized/NotAuthorized";
import DocumentViewModal from "../../components/DocumentViewModal";

const GroupHeader = styled("div")(({ theme }) => ({
    position: "sticky",
    top: 0,
    padding: "4px 8px",
    color: "#181818",
    backgroundColor: "#eee",
    fontWeight: 600,
    fontSize: "12.5px",
}));

const GroupItems = styled("ul")({
    padding: 0,
    margin: 0,
    listStyle: "none",
});

const INITIAL_DOCUMENT = {
    name: "",
    description: "",
    attachment_type: "none",
    url: "",
    files: [],
    status: "private",
};

const DEFAULT_CATEGORY_ID = 1;
const getBooleanOption = (value, fallback = false) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["true", "1", "yes", "on", "enabled"].includes(normalized)) return true;
        if (["false", "0", "no", "off", "disabled"].includes(normalized)) return false;
    }
    return fallback;
};

const ManageDocuments = () => {
    const { t } = useTranslation();
	const [documents, setDocuments] = useState([]);
	const [categories, setCategories] = useState([]);
	const [documentsLoading, setDocumentsLoading] = useState(true);
	const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [machines, setMachines] = useState([]);
    const [machineSearch, setMachineSearch] = useState("");
    const [machinePage, setMachinePage] = useState(1);
    const [machineHasMore, setMachineHasMore] = useState(true);
    const [machinesLoading, setMachinesLoading] = useState(false);
    const [newDoc, setNewDoc] = useState(INITIAL_DOCUMENT);
    const [selectedMachines, setSelectedMachines] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editDocId, setEditDocId] = useState(null);
    const [error, setError] = useState({});
    const [formErrors, setFormErrors] = useState([]); // alert errors
    const [activeStep, setActiveStep] = useState(0);
    const steps = ["Document Details", t('GENERAL.MACHINES_VISIBILITY')];
    const [visibilityScope, setVisibilityScope] = useState("all");
    const [audienceOptions, setAudienceOptions] = useState([]);
    const [audienceLoading, setAudienceLoading] = useState(false);
    const [isSubmittingDocument, setIsSubmittingDocument] = useState(false);
    const [selectedAudienceCustomers, setSelectedAudienceCustomers] = useState([]);
    const [selectedAudienceSites, setSelectedAudienceSites] = useState([]);
    const [selectedDocCategories, setSelectedDocCategories] = useState([]);
    const [tabIndex, setTabIndex] = useState(0);
    const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);
    const [editCategoryModal, setEditCategoryModal] = useState({ open: false, category: null });
    const [assignModal, setAssignModal] = useState({ open: false, category: null, documentIds: [] });
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewDoc, setViewDoc] = useState(null);
    const [categoryDetailsModal, setCategoryDetailsModal] = useState({ open: false, category: null, documents: [] });
    const [deleteCategoryModalState, setDeleteCategoryModalState] = useState({ open: false, category: null });
    const { selectedSite, siteData } = useSite();

    const { hasPermission, user } = useAuth();
    const actorRoleType = useMemo(
        () => String(user?.role?.role_type || user?.role?.role_name || "").trim().toLowerCase(),
        [user?.role?.role_type, user?.role?.role_name]
    );
    const [formSiteNumber, setFormSiteNumber] = useState(selectedSite || "");
    const [fileDropzoneKey, setFileDropzoneKey] = useState(0);
    const [fallbackActorSiteIds, setFallbackActorSiteIds] = useState([]);
    const actorSiteIds = useMemo(() => {
        const fromAllocs = Array.isArray(user?.allocations)
            ? user.allocations.map((a) => Number(a?.site_id)).filter((id) => Number.isFinite(id) && id > 0)
            : [];
        return Array.from(new Set(fromAllocs));
    }, [user?.allocations]);
    useEffect(() => {
        let mounted = true;
        const loadFallbackActorScope = async () => {
            if (Number(user?.user_id) === 1 || actorSiteIds.length > 0 || !user?.user_id) {
                if (mounted) setFallbackActorSiteIds([]);
                return;
            }
            try {
                const resp = await getUserAllocations(user.user_id);
                const allocations = Array.isArray(resp?.data?.allocations) ? resp.data.allocations : [];
                const ids = Array.from(
                    new Set(
                        allocations
                            .map((a) => Number(a?.site_id))
                            .filter((id) => Number.isFinite(id) && id > 0)
                    )
                );
                if (mounted) setFallbackActorSiteIds(ids);
            } catch (_) {
                if (mounted) setFallbackActorSiteIds([]);
            }
        };
        loadFallbackActorScope();
        return () => {
            mounted = false;
        };
    }, [actorSiteIds, user?.user_id]);
    const effectiveActorSiteIds = useMemo(() => {
        if (actorSiteIds.length > 0) return actorSiteIds;
        return fallbackActorSiteIds;
    }, [actorSiteIds, fallbackActorSiteIds]);
    const actorHasAllSiteVisibility = useMemo(
        () =>
            Number(user?.user_id) === 1 ||
            ((actorRoleType === "admin" || getBooleanOption(user?.role?.permissions?.user_management?.manage_all_sites)) &&
                effectiveActorSiteIds.length === 0),
        [user?.user_id, actorRoleType, user?.role?.permissions?.user_management?.manage_all_sites, effectiveActorSiteIds.length]
    );
    const scopedSiteData = useMemo(() => {
        const allSites = Array.isArray(siteData) ? siteData : [];
        if (actorHasAllSiteVisibility) return allSites;
        const allowed = new Set((effectiveActorSiteIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0));
        return allSites.filter((site) => allowed.has(Number(site?.site_id)));
    }, [siteData, actorHasAllSiteVisibility, effectiveActorSiteIds]);
    const allowedSiteIdSet = useMemo(
        () => new Set((scopedSiteData || []).map((site) => Number(site?.site_id)).filter((id) => Number.isFinite(id) && id > 0)),
        [scopedSiteData]
    );
    const selectedSiteRecord = useMemo(
        () =>
            scopedSiteData.find((site) => site.customer_site_number === selectedSite) ||
            scopedSiteData[0] ||
            null,
        [scopedSiteData, selectedSite]
    );
    const selectedSiteId = selectedSiteRecord?.site_id;
    const canCreateDocuments = hasPermission("document_management.create_documents");
    const canEditDocuments = hasPermission("document_management.edit_documents");
    const canDeleteDocuments = hasPermission("document_management.delete_documents");
    const canAssignDocuments = canCreateDocuments || canEditDocuments;
    const canManageDocuments = canCreateDocuments || canEditDocuments || canDeleteDocuments;
    const canManageCategories = canAssignDocuments || canDeleteDocuments;
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB


    useEffect(() => {
        if (openDialog) return;
        setFormSiteNumber(selectedSite || "");
    }, [selectedSite, openDialog]);
    useEffect(() => {
        if (!openDialog) return;
        if (formSiteNumber && scopedSiteData.some((site) => site.customer_site_number === formSiteNumber)) return;
        const fallbackSiteNumber = selectedSiteRecord?.customer_site_number || scopedSiteData?.[0]?.customer_site_number || "";
        setFormSiteNumber(fallbackSiteNumber);
    }, [openDialog, formSiteNumber, scopedSiteData, selectedSiteRecord]);


    const aggregatedCustomerSites = useMemo(() => {
        if (!Array.isArray(scopedSiteData)) return [];
        const map = new Map();
        scopedSiteData.forEach((site) => {
            const cusId = Number(site?.cus_id);
            if (!Number.isFinite(cusId)) return;
            const customerName = site.parent_customer_name || site.customer_name || site.customer_site_name || `${t('UI_TEXT.CUSTOMER')} ${cusId}`;
            const customerNumber = site.parent_customer_number || site.customer_number || "";
            const siteEntry = {
                site_id: site.site_id,
                cus_id: cusId,
                customer_site_name: site.customer_site_name || site.address_name_ln || site.site_name || "",
                customer_site_number: site.customer_site_number,
            };
            if (!map.has(cusId)) {
                map.set(cusId, {
                    cus_id: cusId,
                    customer_name: customerName,
                    customer_number: customerNumber,
                    sites: [siteEntry],
                });
            } else {
                const existing = map.get(cusId);
                const exists = existing.sites.some((s) => Number(s.site_id) === Number(siteEntry.site_id));
                if (!exists) existing.sites.push(siteEntry);
            }
        });
        return Array.from(map.values()).map((entry) => ({
            ...entry,
            sites: entry.sites.sort((a, b) => (a.customer_site_name || "").localeCompare(b.customer_site_name || "")),
        })).sort((a, b) => (a.customer_name || "").localeCompare(b.customer_name || ""));
    }, [scopedSiteData, t]);

    const combinedAudienceOptions = useMemo(() => {
        if (!Array.isArray(audienceOptions) || audienceOptions.length === 0) return aggregatedCustomerSites;
        const map = new Map();
        aggregatedCustomerSites.forEach((entry) => {
            map.set(entry.cus_id, { ...entry });
        });
        audienceOptions.forEach((entry) => {
            const cusId = Number(entry?.cus_id);
            if (!Number.isFinite(cusId)) return;
            if (!map.has(cusId)) {
                map.set(cusId, {
                    cus_id: cusId,
                    customer_name: entry.customer_name,
                    customer_number: entry.customer_number,
                    sites: Array.isArray(entry.sites)
                        ? entry.sites.sort((a, b) => (a.customer_site_name || "").localeCompare(b.customer_site_name || ""))
                        : [],
                });
            } else {
                const existing = map.get(cusId);
                const siteMap = new Map((existing.sites || []).map((site) => [site.site_id, site]));
                (entry.sites || []).forEach((site) => {
                    if (!siteMap.has(site.site_id)) {
                        siteMap.set(site.site_id, site);
                    }
                });
                existing.sites = Array.from(siteMap.values()).sort((a, b) => (a.customer_site_name || "").localeCompare(b.customer_site_name || ""));
                existing.customer_name = entry.customer_name || existing.customer_name;
                existing.customer_number = entry.customer_number || existing.customer_number;
                map.set(cusId, existing);
            }
        });
        return Array.from(map.values()).sort((a, b) => (a.customer_name || "").localeCompare(b.customer_name || ""));
    }, [audienceOptions, aggregatedCustomerSites]);

    const hasMachines = selectedMachines.length > 0;
    const visibilityAllLabel = hasMachines
        ? (t('LABELS.VISIBILITY_ALL_WITH_MACHINES') || "Visible to all customer locations that have the selected machines provisioned.")
        : (t('LABELS.VISIBILITY_ALL_CUSTOMERS') || "Visible to all customer locations.");
    const customVisibilityLabel = t('LABELS.VISIBILITY_CUSTOM_LOCATIONS') || "Assign specific customer location visibility.";
    const allCustomersNote = t('UI_TEXT.ALL_CUSTOMERS_SEE_DOCUMENT') || "All customer locations will be able to see the document.";
    const matchingCustomersNote = t('UI_TEXT.MATCHING_CUSTOMERS_SEE_DOCUMENT') || "All customer locations with the selected machines provisioned will be able to see the document.";
    const categoryCreationHint = t('UI_TEXT.CATEGORY_CREATION_HINT') || "Start typing and press Enter to create a new category.";

    const flattenLocations = useCallback((customersSource = []) => {
        const rows = [];
        customersSource.forEach((customer) => {
            const cusId = Number(customer?.cus_id);
            if (!Number.isFinite(cusId)) return;
            const customerName = customer.customer_name || customer.customer_site_name || `${t('UI_TEXT.CUSTOMER')} ${cusId}`;
            const customerNumber = customer.customer_number || "";
            (customer?.sites || []).forEach((site) => {
                const siteId = Number(site?.site_id);
                if (!Number.isFinite(siteId)) return;
                const siteName = site.customer_site_name || site.address_name_ln || site.site_name || `Site ${siteId}`;
                rows.push({
                    site_id: siteId,
                    cus_id: cusId,
                    customer_name: customerName,
                    customer_number: customerNumber,
                    customer_site_name: siteName,
                    customer_site_number: site.customer_site_number || site.site_number || "",
                });
            });
        });
        return rows;
    }, [t]);

    const allLocationRows = useMemo(
        () => flattenLocations(aggregatedCustomerSites),
        [aggregatedCustomerSites, flattenLocations]
    );

    const machineLocationRows = useMemo(
        () => flattenLocations(audienceOptions).filter((row) => allowedSiteIdSet.has(Number(row.site_id))),
        [audienceOptions, flattenLocations, allowedSiteIdSet]
    );

    const currentLocationRows = useMemo(
        () => (hasMachines ? machineLocationRows : allLocationRows),
        [hasMachines, machineLocationRows, allLocationRows]
    );

    const locationRowsByCustomer = useMemo(() => {
        const map = new Map();
        currentLocationRows.forEach((row) => {
            const cusId = Number(row.cus_id);
            if (!map.has(cusId)) map.set(cusId, []);
            map.get(cusId).push(row);
        });
        map.forEach((rows) => rows.sort((a, b) => (a.customer_site_name || "").localeCompare(b.customer_site_name || "")));
        return map;
    }, [currentLocationRows]);

    const locationSelectionModel = useMemo(() => {
        const ids = new Set();
        selectedAudienceSites.forEach((site) => {
            if (Number.isFinite(Number(site.site_id))) ids.add(Number(site.site_id));
        });
        selectedAudienceCustomers.forEach((customer) => {
            const rows = locationRowsByCustomer.get(Number(customer.cus_id)) || [];
            rows.forEach((row) => {
                if (Number.isFinite(Number(row.site_id))) ids.add(Number(row.site_id));
            });
        });
        return Array.from(ids);
    }, [selectedAudienceSites, selectedAudienceCustomers, locationRowsByCustomer]);

    useEffect(() => {
        if (!openDialog || visibilityScope !== "custom") return;
        if (audienceLoading && hasMachines) return;
        if (currentLocationRows.length === 0) {
            if (isEditing && hasMachines) return;
            setSelectedAudienceCustomers([]);
            setSelectedAudienceSites([]);
            return;
        }
        const validSiteIds = new Set(currentLocationRows.map((row) => Number(row.site_id)));
        const validCustomerIds = new Set(Array.from(locationRowsByCustomer.keys()).map(Number));
        setSelectedAudienceSites((prev) => prev.filter((site) => validSiteIds.has(Number(site.site_id))));
        setSelectedAudienceCustomers((prev) => prev.filter((cust) => validCustomerIds.has(Number(cust.cus_id))));
    }, [currentLocationRows, locationRowsByCustomer, openDialog, visibilityScope, audienceLoading, hasMachines, isEditing]);

    const handleLocationSelectionChange = useCallback(
        (selectedIds = []) => {
            if (visibilityScope !== "custom") return;
            const selectedSet = new Set(selectedIds.map((id) => Number(id)));
            const nextCustomers = [];
            const nextSites = [];

            locationRowsByCustomer.forEach((rows, cusId) => {
                const matched = rows.filter((row) => selectedSet.has(Number(row.site_id)));
                if (matched.length === rows.length && matched.length > 0) {
                    const ref = matched[0];
                    nextCustomers.push({
                        cus_id: cusId,
                        customer_name: ref.customer_name,
                        customer_number: ref.customer_number,
                    });
                } else {
                    matched.forEach((row) => {
                        nextSites.push({
                            site_id: row.site_id,
                            cus_id: row.cus_id,
                            customer_site_name: row.customer_site_name,
                            customer_site_number: row.customer_site_number,
                            parent_customer_name: row.customer_name,
                        });
                    });
                }
            });

            setSelectedAudienceCustomers(nextCustomers);
            setSelectedAudienceSites(nextSites);
        },
        [locationRowsByCustomer, visibilityScope]
    );

    const locationColumns = useMemo(
        () => [

            { field: "customer_site_number", headerName: t('TABLE_HEADERS.SITE_NUMBER') || "Site Number", width: 160 },
            { field: "customer_site_name", headerName: t('TABLE_HEADERS.SITE') || t('SITE_MANAGEMENT.SITE'), flex: 1 },
            { field: "customer_number", headerName: t('TABLE_HEADERS.CUSTOMER_NUMBER') || t('UI_TEXT.CUSTOMER'), flex: 1 },
            { field: "customer_name", headerName: t('TABLE_HEADERS.CUSTOMER_NAME') || t('UI_TEXT.CUSTOMER'), flex: 1 },
        ],
        [t]
    );

    const scopedCategoryIds = useMemo(() => {
        const ids = new Set();
        documents.forEach((doc) => {
            if (!doc || !Array.isArray(doc.categories)) return;
            doc.categories.forEach((cat) => {
                const id = Number(cat?.category_id || cat?.id);
                if (Number.isFinite(id)) ids.add(id);
            });
        });
        return ids;
    }, [documents]);

    const scopedCategories = useMemo(() => {
        return (categories || []).filter((category) => {
            const id = Number(category?.id || category?.category_id);
            if (!Number.isFinite(id)) return false;
            if (id === DEFAULT_CATEGORY_ID || category?.is_default) return true;
            return scopedCategoryIds.has(id);
        });
    }, [categories, scopedCategoryIds]);

    const categorySelectOptions = useMemo(() => {
        return (scopedCategories || []).map((category) => ({
            id: category.id,
            label: category.category_name,
        }));
    }, [scopedCategories]);

    const assignSelectedDocs = useMemo(() => {
        if (!assignModal.open) return [];
        const docMap = new Map(documents.map((doc) => [doc.id, doc]));
        return assignModal.documentIds.map((docId) => docMap.get(docId)).filter(Boolean);
    }, [assignModal, documents]);

    const documentsByCategoryId = useMemo(() => {
        const map = new Map();
        documents.forEach((doc) => {
            if (!doc || !Array.isArray(doc.categories)) return;
            doc.categories.forEach((cat) => {
                const id = Number(cat.category_id || cat.id);
                if (!Number.isFinite(id)) return;
                if (!map.has(id)) map.set(id, []);
                map.get(id).push(doc);
            });
        });
        return map;
    }, [documents]);

    const defaultCategory = useMemo(
        () => scopedCategories.find((cat) => cat.is_default) || scopedCategories.find((cat) => Number(cat?.id) === DEFAULT_CATEGORY_ID),
        [scopedCategories]
    );

    const categoryGridRows = useMemo(() => {
        return (scopedCategories || []).map((category) => {
            const id = Number(category?.id || category?.category_id);
            const scopedCount = Number.isFinite(id) ? (documentsByCategoryId.get(id)?.length || 0) : 0;
            return {
                ...category,
                document_count: scopedCount,
            };
        });
    }, [scopedCategories, documentsByCategoryId]);

    const exclusiveDocsForDelete = useMemo(() => {
        if (!deleteCategoryModalState.category) return [];
        return documents.filter((doc) => {
            if (!doc || !Array.isArray(doc.categories)) return false;
            const categoryIds = doc.categories
                .map((cat) => Number(cat.category_id || cat.id))
                .filter((id) => Number.isFinite(id));
            return (
                categoryIds.length === 1 &&
                categoryIds[0] === deleteCategoryModalState.category.id
            );
        });
    }, [deleteCategoryModalState.category, documents]);

    const categoryDocumentsForDelete = useMemo(() => {
        if (!deleteCategoryModalState.category) return [];
        const id = Number(deleteCategoryModalState.category.id);
        if (!Number.isFinite(id)) return [];
        return documentsByCategoryId.get(id) || [];
    }, [deleteCategoryModalState.category, documentsByCategoryId]);

    const handleCategorySelection = (event, newValue) => {
        const normalized = (newValue || []).map((entry) => {
            if (typeof entry === "string") {
                return { id: null, label: entry, isNew: true };
            }
            if (entry?.inputValue) {
                return { id: null, label: entry.inputValue, isNew: true };
            }
            const id = entry?.id ?? entry?.category_id ?? null;
            const label = entry?.label || entry?.category_name || "";
            return { id, label, isNew: !id };
        });
        setSelectedDocCategories(normalized);
        setError((prev) => ({ ...prev, categories: undefined }));
    };

    const handleOpenAddCategoryModal = () => {
        if (!canCreateDocuments) return;
        setAddCategoryModalOpen(true);
    };

    const handleOpenEditCategoryModal = (category) => {
        if (!category || Number(category?.id) === DEFAULT_CATEGORY_ID) return;
        if (!canEditDocuments) return;
        setEditCategoryModal({ open: true, category });
    };

    const handleCreateCategory = async (name) => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            throw new Error(t('UI_TEXT.CATEGORY_REQUIRED'));
        }
        try {
            await createDocumentCategory({ category_name: trimmedName });
            Swal.fire({
                icon: 'success',
                title: t('ALERT_MESSAGES.SUCCESS'),
                text: t('ALERT_MESSAGES.CATEGORY_CREATED') || 'Category created successfully',
            });
            fetchCategories();
        } catch (error) {
            const message = error?.response?.data?.message || t('ALERT_MESSAGES.ERROR');
            throw new Error(message);
        }
    };

    const handleEditCategorySubmit = async (name) => {
        if (!editCategoryModal.category) {
            throw new Error(t('ALERT_MESSAGES.ERROR'));
        }
        const trimmedName = name.trim();
        if (!trimmedName) {
            throw new Error(t('UI_TEXT.CATEGORY_REQUIRED'));
        }
        try {
            await updateDocumentCategory(editCategoryModal.category.id, { category_name: trimmedName });
            Swal.fire({
                icon: 'success',
                title: t('ALERT_MESSAGES.SUCCESS'),
                text: t('ALERT_MESSAGES.CATEGORY_UPDATED') || 'Category updated successfully',
            });
            fetchCategories();
        } catch (error) {
            const message = error?.response?.data?.message || t('ALERT_MESSAGES.ERROR');
            throw new Error(message);
        }
    };

    const handleDeleteCategoryRequest = (category) => {
        if (!category) return;
        if (Number(category.id) === DEFAULT_CATEGORY_ID || category?.is_default) {
            Swal.fire({
                icon: "info",
                title: t('ALERT_MESSAGES.WARNING_TITLE') || t('ALERT_MESSAGES.WARNING'),
                text: t('UI_TEXT.DEFAULT_CATEGORY_LOCKED'),
            });
            return;
        }
        if (!canDeleteDocuments) return;
        setDeleteCategoryModalState({ open: true, category });
    };

    const handleOpenAssignModal = (category) => {
        if (!canAssignDocuments) return;
        setAssignModal({
            open: true,
            category,
            documentIds: Array.isArray(category?.documents) ? category.documents.map((doc) => doc.document_id) : [],
        });
    };

    const handleAssignModalClose = () => {
        setAssignModal({ open: false, category: null, documentIds: [] });
    };

    const handleAssignDocumentsChange = (event, value) => {
        const ids = (value || []).map((doc) => doc.id || doc.document_id).filter(Boolean);
        setAssignModal((prev) => ({ ...prev, documentIds: ids }));
    };

    const handleAssignDocumentsSave = async () => {
        if (!assignModal.category) return;
        try {
            await updateCategoryDocuments(assignModal.category.id, { document_ids: assignModal.documentIds });
            Swal.fire({ icon: 'success', title: t('ALERT_MESSAGES.SUCCESS'), text: t('ALERT_MESSAGES.CATEGORY_UPDATED') || 'Category updated successfully' });
            handleAssignModalClose();
            fetchCategories();
            fetchDocuments();
        } catch (error) {
            const message = error?.response?.data?.message || t('ALERT_MESSAGES.ERROR');
            Swal.fire({ icon: 'error', title: t('ALERT_MESSAGES.ERROR'), text: message });
        }
    };

    const handleRemoveDocumentFromCategory = async (categoryId, documentId) => {
        if (!categoryId || !documentId) return;
        const confirmation = await Swal.fire({
            title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
            text: t('UI_TEXT.REMOVE_FROM_CATEGORY_CONFIRM'),
            icon: 'warning',
            showCancelButton: true,
        });
        if (!confirmation.isConfirmed) return;
        try {
            const docsForCategory = documentsByCategoryId.get(categoryId) || [];
            const remainingIds = docsForCategory
                .map((doc) => doc.id)
                .filter((id) => Number.isFinite(id) && id !== documentId);
            await updateCategoryDocuments(categoryId, { document_ids: remainingIds });
            Swal.fire({ icon: 'success', title: t('ALERT_MESSAGES.SUCCESS'), text: t('ALERT_MESSAGES.CATEGORY_UPDATED') });
            fetchCategories();
            fetchDocuments();
        } catch (error) {
            const message = error?.response?.data?.message || t('ALERT_MESSAGES.ERROR');
            Swal.fire({ icon: 'error', title: t('ALERT_MESSAGES.ERROR'), text: message });
        }
    };

    const handleConfirmDeleteCategory = async ({ reassignCategoryId }) => {
        if (!deleteCategoryModalState.category) return;
        try {
            await deleteDocumentCategory(deleteCategoryModalState.category.id, {
                reassign_category_id: reassignCategoryId,
                fallback_to_uncategorized: !reassignCategoryId,
            });
            Swal.fire({
                icon: 'success',
                title: t('ALERT_MESSAGES.DELETED'),
                text: t('ALERT_MESSAGES.CATEGORY_DELETED') || 'Category deleted successfully',
            });
            const deletedCategoryId = deleteCategoryModalState.category.id;
            setDeleteCategoryModalState({ open: false, category: null });
            if (categoryDetailsModal.category?.id === deletedCategoryId) {
                handleCloseCategoryDetails();
            }
            fetchCategories();
            fetchDocuments();
        } catch (error) {
            const message = error?.response?.data?.message || t('ALERT_MESSAGES.ERROR');
            throw new Error(message);
        }
    };

    const handleOpenCategoryDetails = (category) => {
        if (!category) return;
        const docsForCategory = documentsByCategoryId.get(category.id) || [];
        setCategoryDetailsModal({ open: true, category, documents: docsForCategory });
    };

    const handleCloseCategoryDetails = () => {
        setCategoryDetailsModal({ open: false, category: null, documents: [] });
    };

    const handleCloseDeleteCategoryModal = () => {
        setDeleteCategoryModalState({ open: false, category: null });
    };

    const handleViewDocument = (doc) => {
        if (!doc) return;
        setViewDoc(doc);
        setViewModalOpen(true);
    };

    const handleCloseViewDocument = () => {
        setViewDoc(null);
        setViewModalOpen(false);
    };

    useEffect(() => {
        if (!categoryDetailsModal.open || !categoryDetailsModal.category) return;
        const docsForCategory = documentsByCategoryId.get(categoryDetailsModal.category.id) || [];
        setCategoryDetailsModal((prev) => {
            if (!prev.open || prev.category?.id !== categoryDetailsModal.category.id) {
                return prev;
            }
            const prevDocs = prev.documents || [];
            const sameLength = prevDocs.length === docsForCategory.length;
            const sameItems =
                sameLength &&
                prevDocs.every((doc, idx) => doc?.id === docsForCategory[idx]?.id);
            if (sameItems) return prev;
            return { ...prev, documents: docsForCategory };
        });
    }, [categoryDetailsModal.open, categoryDetailsModal.category, documentsByCategoryId]);

    const handleAddDocumentFromCategory = (category) => {
        if (!category || !canCreateDocuments) return;
        if (categoryDetailsModal.open) {
            handleCloseCategoryDetails();
        }
        resetFormState();
        setSelectedDocCategories([{ id: category.id, label: category.category_name, isNew: false }]);
        setIsEditing(false);
        setEditDocId(null);
        setOpenDialog(true);
        setTabIndex(0);
    };

    const resetFormState = () => {
        setNewDoc(INITIAL_DOCUMENT);
        setSelectedDocCategories([]);
        setSelectedMachines([]);
        setSelectedAudienceCustomers([]);
        setSelectedAudienceSites([]);
        setAudienceOptions([]);
        setVisibilityScope("all");
        setError({});
        setFormErrors([]);
        setActiveStep(0);
        setMachineSearch("");
        setFormSiteNumber(selectedSite || "");
        setFileDropzoneKey((k) => k + 1);
    };

    const updateErrorsForKeys = (keys, newErrors) => {
        setError((prev) => {
            const next = { ...prev };
            keys.forEach((key) => {
                delete next[key];
            });
            if (Object.keys(newErrors).length === 0) return next;
            return { ...next, ...newErrors };
        });
    };

    const validateStepOne = () => {
        const newErrors = {};
        if (!newDoc.name) newErrors["name"] = t('UI_TEXT.NAME_REQUIRED');
        if (selectedDocCategories.length === 0) newErrors["categories"] = t('UI_TEXT.CATEGORY_REQUIRED');
        if (!isEditing && newDoc.attachment_type === 'file' && (!newDoc.files || newDoc.files.length === 0)) newErrors["file"] = t('UI_TEXT.FILE_REQUIRED');
        if (newDoc.attachment_type === 'url') {
            const isValidUrl = (u) => { try { const url = new URL(u); return url.protocol === 'http:' || url.protocol === 'https:'; } catch { return false; } };
            if (!newDoc.url) newErrors['url'] = 'URL is required';
            else if (!isValidUrl(newDoc.url)) newErrors['url'] = 'Enter a valid http(s) URL';
        }
        updateErrorsForKeys(["name", "categories", "file", "url", "site"], newErrors);
        if (Object.keys(newErrors).length > 0) {
            setFormErrors(Object.values(newErrors));
            return false;
        }
        setFormErrors([]);
        return true;
    };

    const buildVisibilityPayload = () => {
        if (visibilityScope !== "custom") return [];
        const customerEntries = selectedAudienceCustomers.map((cust) => ({
            cus_id: cust.cus_id,
            all_sites: true,
        }));
        const blockedCustomers = new Set(selectedAudienceCustomers.map((cust) => cust.cus_id));
        const seenSites = new Set();
        const siteEntries = selectedAudienceSites
            .filter((site) => site?.site_id && !blockedCustomers.has(site.cus_id))
            .filter((site) => {
                if (seenSites.has(site.site_id)) return false;
                seenSites.add(site.site_id);
                return true;
            })
            .map((site) => ({
                cus_id: site.cus_id,
                site_id: site.site_id,
                all_sites: false,
            }));
        return [...customerEntries, ...siteEntries];
    };

    const validateStepTwo = () => {
        const newErrors = {};
        if (visibilityScope === "custom") {
            const payload = buildVisibilityPayload();
            if (payload.length === 0) newErrors["visibility"] = t('GENERAL.SELECT_CUSTOMERS_OR_SITES_TO_SHARE_THIS_DOCUMENT_WITH');
        }
        updateErrorsForKeys(["machines", "visibility"], newErrors);
        if (Object.keys(newErrors).length > 0) {
            setFormErrors(Object.values(newErrors));
            return false;
        }
        setFormErrors([]);
        return true;
    };

    const columns = [
        { field: "name", headerName: t('TABLE_HEADERS.NAME'), width: 300 },
        {
            field: "categories",
            headerName: t('LABELS.CATEGORY'),
            width: 300,
            renderCell: (params) => {
                const cats = Array.isArray(params.row?.categories) ? params.row.categories : [];
                if (cats.length === 0) {
                    return (
                        <Box sx={{ display: "flex", justifyContent: 'flex-start', alignItems: 'center', width: '100%', flexWrap: 'wrap', height: '100%' }}>
                            <Typography variant="body2" color="text.secondary">{t('UI_TEXT.NO_CATEGORY_ASSIGNED') || 'Uncategorized'}</Typography>
                        </Box>
                    )
                }
                return (
                    <Box sx={{ display: "flex", justifyContent: 'flex-start', alignItems: 'center', width: '100%', flexWrap: 'wrap', height: '100%', columnGap: 0.75 }}>
                        {cats.map((cat) => (
                            <Chip key={`${params.id}-${cat.category_id || cat.id}`} size="small" label={cat.category_name} />
                        ))}
                    </Box>
                );
            }
        },
        {
            field: "description",
            headerName: t('TABLE_HEADERS.DESCRIPTION'),
            flex: 2,
            renderCell: (params) => {
                // Remove HTML tags and trim whitespace
                const plainText = (params.value || '')
                    .replace(/<[^>]+>/g, '') // strip HTML tags
                    .trim();

                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center', // vertically center
                            height: '100%',
                            width: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: '1.3em',
                                maxHeight: '2.6em',
                                width: '100%',
                                color:
                                    plainText === t('UI_TEXT.NO_DESCRIPTION_AVAILABLE_1')
                                        ? 'text.secondary'
                                        : 'inherit',
                                wordBreak: 'break-word',
                            }}
                        >
                            {plainText || ''}
                        </Box>
                    </Box>
                );
            },
            cellClassName: (params) => {
                const plainText = (params.value || '').replace(/<[^>]+>/g, '').trim();
                return plainText === t('UI_TEXT.NO_DESCRIPTION_AVAILABLE_1') ? "gray-text" : "";
            },
        },
        {
            field: "visibility_status",
            headerName: t('TABLE_HEADERS.STATUS') || 'Status',
            width: 140,
            renderCell: (params) => {
                const value = params.value === 'public' ? 'public' : 'private';
                const label = value === 'public' ? (t('LABELS.PUBLIC') || 'Public') : (t('LABELS.PRIVATE') || 'Private');
                return (
                    <Chip
                        size="small"
                        label={label}
                        color={value === 'public' ? 'success' : 'default'}
                        variant="outlined"
                    />
                );
            },
        },
        {
            field: "actions",
            headerName: t('TABLE_HEADERS.ACTIONS'),
            width: 100,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => {
                if (!canEditDocuments && !canDeleteDocuments) return null;
                return (
                    <Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Box sx={{ display: "flex", justifyContent: 'center', alignItems: 'center' }}>
                            {canEditDocuments && (
                                <IconButton color="primary" onClick={(event) => { event.stopPropagation(); handleEditOpen(params.row); }}>
                                    <EditIcon />
                                </IconButton>
                            )}
                            {canDeleteDocuments && (
                                <IconButton color="error" onClick={(event) => { event.stopPropagation(); handleDeleteDocument(params.row.id); }}>
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                );
            },
        },
    ];

    const categoryColumns = [
        { field: "category_name", headerName: t('LABELS.CATEGORY'), flex: 2.5 },
        { field: "document_count", headerName: t('TABLE_HEADERS.DOCUMENTS') || 'Documents #', width: 200 },
        {
            field: "actions",
            headerName: t('TABLE_HEADERS.ACTIONS'),
            flex: 1,
            renderCell: (params) => {
                const isDefault = Number(params.row?.id) === DEFAULT_CATEGORY_ID || params.row?.is_default;
                return (
                    <Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', height: '100%' }}>
                        <Box sx={{ display: "flex", justifyContent: 'flex-start', alignItems: 'center', columnGap: 1.5 }}>
                            {canAssignDocuments && (
                                <Button size="small" variant="outlined" onClick={(event) => { event.preventDefault(); event.stopPropagation(); handleOpenAssignModal(params.row); }} >
                                    {t('BUTTON.ASSIGN') || 'Assign'}
                                </Button>
                            )}
                            {canCreateDocuments && (
                                <Button size="small" variant="outlined" onClick={(event) => { event.preventDefault(); event.stopPropagation(); handleAddDocumentFromCategory(params.row); }}>
                                    {t('BUTTON.ADD_DOCUMENT') || 'Add Doc'}
                                </Button>
                            )}l
                            <Box sx={{ display: 'flex' }}>
                                {canEditDocuments && !isDefault && (
                                    <IconButton color="primary" onClick={(event) => { event.preventDefault(); event.stopPropagation(); handleOpenEditCategoryModal(params.row); }}>
                                        <EditIcon />
                                    </IconButton>
                                )}
                                {canDeleteDocuments && !isDefault && (
                                    <IconButton color="error" onClick={(event) => { event.preventDefault(); event.stopPropagation(); handleDeleteCategoryRequest(params.row); }}>
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    </Box >
                );
            },
        },
    ];

    const [selectedRows, setSelectedRows] = useState([]);

    const customButtonsCategory = [
        canCreateDocuments
            ? {
                label: t('LABELS.ADD_CATEGORY'),
                icon: <AddCircle />,
                onClick: handleOpenAddCategoryModal,
            }
            : null,
    ].filter(Boolean);

    const customButtons = [
        canCreateDocuments
            ? {
                label: t('LABELS.ADD_DOCUMENTS'),
                icon: <AddCircle />,
                onClick: () => {
                    resetFormState();
                    setIsEditing(false);
                    setEditDocId(null);
                    setOpenDialog(true);
                },
            }
            : null,
        canCreateDocuments
            ? {
                label: t('LABELS.ADD_CATEGORY'),
                icon: <AddCircle />,
                onClick: handleOpenAddCategoryModal,
            }
            : null,
        canDeleteDocuments
            ? {
                label: 'Delete Selected',
                icon: <DeleteIcon />,
                color: "error",
                disabled: !selectedRows || selectedRows.length === 0,
                onClick: async () => {
                    if (!selectedRows || selectedRows.length === 0) return;
                    const confirmation = await Swal.fire({
                        title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
                        text: 'Selected documents will be permanently deleted',
                        icon: 'warning',
                        showCancelButton: true,
                    });
                    if (!confirmation.isConfirmed) return;
                    try {
                        await Promise.all(selectedRows.map((id) => deleteDocument(id)));
                        setDocuments((prev) => prev.filter((d) => !selectedRows.includes(d.id)));
                        setSelectedRows([]);
                        Swal.fire({ icon: 'success', title: t('ALERT_MESSAGES.DELETED'), text: t('ALERT_MESSAGES.DOCUMENT_DELETED_SUCCESSFULLY') });
                    } catch (e) {
                        Swal.fire({ icon: 'error', title: t('ALERT_MESSAGES.ERROR'), text: t('ALERT_MESSAGES.COULD_NOT_DELETE_DOCUMENT') });
                    }
                },
            }
            : null,
    ].filter(Boolean);

    const fetchDocuments = useCallback(async () => {
        if (!selectedSiteId) {
            setDocuments([]);
            setDocumentsLoading(false);
            return;
        }
        setDocumentsLoading(true);
        try {
            const docRes = await getDocumentsMetaData(selectedSiteId);
            setDocuments(docRes.data);
        } catch (err) {
            console.error("Error fetching data", err);
            setDocuments([]);
        } finally {
            setDocumentsLoading(false);
        }
    }, [selectedSiteId]);

    const fetchCategories = useCallback(async () => {
        setCategoriesLoading(true);
        try {
            const catRes = await getDocumentCategories();
            const responseCategories = Array.isArray(catRes?.data) ? catRes.data : [];
            setCategories(responseCategories);
        } catch (err) {
            console.error("Error fetching categories", err);
            setCategories([]);
        } finally {
            setCategoriesLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDocuments();
        fetchCategories();
    }, [fetchDocuments, fetchCategories]);

    useEffect(() => {
        if (!selectedMachines || selectedMachines.length === 0) return;
        setMachines((prev) => {
            const map = new Map(prev.map((m) => [m.machine_id, m]));
            selectedMachines.forEach((machine) => {
                if (machine?.machine_id) {
                    map.set(machine.machine_id, machine);
                }
            });
            return Array.from(map.values());
        });
    }, [selectedMachines]);

    useEffect(() => {
        if (!openDialog) return;
        setMachineSearch("");
        fetchMachineResults(1, "");
    }, [openDialog]);

    useEffect(() => {
        if (!openDialog) return;
        const ids = selectedMachines.map((machine) => machine?.machine_id).filter(Boolean);
        if (ids.length === 0) {
            setAudienceOptions([]);
            return;
        }
        let isCancelled = false;
        const loadAudience = async () => {
			try {
                setAudienceLoading(true);
                const resp = await getDocumentAudience({ machine_ids: ids });
                if (!isCancelled) {
                    setAudienceOptions(Array.isArray(resp?.data) ? resp.data : []);
                }
            } catch (err) {
                console.error(err);
                if (!isCancelled) setAudienceOptions([]);
            } finally {
                if (!isCancelled) setAudienceLoading(false);
			}
		};
        loadAudience();
        return () => {
            isCancelled = true;
        };
    }, [openDialog, selectedMachines]);

    const fetchMachineResults = async (page = 1, q = "") => {
        try {
            setMachinesLoading(true);
            const resp = await searchMachinesApi(q, page, 50);
            const rows = Array.isArray(resp?.data) ? resp.data : [];
            const total = resp?.pagination?.total || 0;
            setMachines((prev) => {
                if (page === 1) return rows;
                const map = new Map(prev.map((m) => [m.machine_id, m]));
                rows.forEach((machine) => {
                    if (machine?.machine_id) {
                        map.set(machine.machine_id, machine);
                    }
                });
                return Array.from(map.values());
            });
            setMachineHasMore(page * 50 < total);
            setMachinePage(page);
        } catch (err) {
            console.error(err);
        } finally {
            setMachinesLoading(false);
        }
    };

    const handleSaveDocument = async () => {
        const resolvedSite =
            (formSiteNumber ? scopedSiteData.find((i) => i.customer_site_number === formSiteNumber) : null) ||
            selectedSiteRecord ||
            scopedSiteData[0] ||
            null;
        const site_id = resolvedSite?.site_id;
        const stepOneValid = validateStepOne();
        const stepTwoValid = validateStepTwo();
        if (!stepOneValid) {
            setActiveStep(0);
            return;
        }
        if (!stepTwoValid) {
            setActiveStep(1);
            return;
        }

        if (!site_id) {
            const siteError = t('UI_TEXT.SITE_REQUIRED') || 'Site is required';
            setError((prev) => ({ ...prev, site: siteError }));
            setFormErrors([siteError]);
            setActiveStep(0);
            return;
        }

        if (isEditing && !editDocId) {
            Swal.fire({
                icon: "error",
                title: t('ALERT_MESSAGES.ERROR'),
                text: t('ALERT_MESSAGES.SOMETHING_WRONG_PLEASE_TRY_AGAIN'),
            });
            return;
        }

        const formData = new FormData();
        formData.append("name", newDoc.name);
        formData.append("description", newDoc.description);
        formData.append("attachment_type", newDoc.attachment_type);
        if (newDoc.attachment_type === 'file' && newDoc.files?.[0]) {
            formData.append("file", newDoc.files[0]);
        }
        if (newDoc.attachment_type === 'url' && newDoc.url) {
            formData.append("attachment", newDoc.url);
        }
        formData.append("site_id", site_id);
        const existingCategoryIds = selectedDocCategories.filter((cat) => cat.id).map((cat) => cat.id);
        const newCategoryNames = selectedDocCategories.filter((cat) => !cat.id).map((cat) => cat.label);
        formData.append("category_ids", JSON.stringify(existingCategoryIds));
        formData.append("new_categories", JSON.stringify(newCategoryNames));
        const selectedMachineIds = selectedMachines.map((machine) => machine.machine_id);
        formData.append("assigned_machines", selectedMachineIds);
        formData.append("visibility_scope", visibilityScope);
        formData.append("visibility_targets", JSON.stringify(buildVisibilityPayload()));
        formData.append("visibility_status", newDoc.status || "private");

        try {
            setIsSubmittingDocument(true);
            const resp = isEditing
                ? await updateDocument(editDocId, formData)
                : await uploadDocument(formData);

            if (resp?.status) {
                await fetchDocuments();
                fetchCategories();
                resetFormState();
                setOpenDialog(false);
                Swal.fire({
                    icon: "success",
                    title: t('ALERT_MESSAGES.SUCCESS'),
                    text: isEditing
                        ? t('ALERT_MESSAGES.DOCUMENT_UPDATED', 'Document updated successfully')
                        : t('ALERT_MESSAGES.DOCUMENT_UPLOADED_SUCCESSFULLY', 'Document uploaded successfully'),
                });
            } else {
                const respMessage = String(resp?.message || resp?.error_message || "");
                const isDuplicate =
                    resp?.error_code === "DUPLICATE_DOCUMENT_NAME" ||
                    resp?.error?.code === "23505" ||
                    resp?.error?.constraint === "documents_name_key" ||
                    respMessage.toLowerCase().includes("duplicate") ||
                    respMessage.toLowerCase().includes("already exists");
                if (isDuplicate) {
                    const msg = respMessage || t('ALERT_MESSAGES.DUPLICATE_DOCUMENT_NAME_CATEGORY');
                    setError((prevErrors) => ({ ...prevErrors, name: msg }));
                    setFormErrors([msg]);
                    Swal.fire({
                        icon: "error",
                        title: t('ALERT_MESSAGES.ERROR'),
                        text: msg,
                    });
                } else {
                    const msg = respMessage || t('ALERT_MESSAGES.SOMETHING_WRONG_PLEASE_TRY_AGAIN');
                    Swal.fire({
                        icon: "error",
                        title: t('ALERT_MESSAGES.ERROR'),
                        text: msg,
                    });
                    setOpenDialog(false);
                    resetFormState();
                }
            }
        } catch (err) {
            console.error(err);
            const statusCode = err.response?.status;
            const errMessage = String(err.response?.data?.message || "");
            const errorCode = err.response?.data?.error_code;
            const isDuplicate =
                (statusCode === 400 || statusCode === 501) &&
                (errorCode === "DUPLICATE_DOCUMENT_NAME" ||
                    err.response?.data?.error?.code === "23505" ||
                    err.response?.data?.error?.constraint === "documents_name_key" ||
                    errMessage.toLowerCase().includes("duplicate") ||
                    errMessage.toLowerCase().includes("already exists"));
            if (isDuplicate) {
                const msg = errMessage || t('ALERT_MESSAGES.DUPLICATE_DOCUMENT_NAME_CATEGORY');
                setError((prevErrors) => ({ ...prevErrors, name: msg }));
                setFormErrors([msg]);
                Swal.fire({
                    icon: "error",
                    title: t('ALERT_MESSAGES.ERROR'),
                    text: msg,
                });
            } else {
                setOpenDialog(false);
                resetFormState();
                Swal.fire({
                    icon: "error",
                    title: t('ALERT_MESSAGES.ERROR'),
                    text: t('ALERT_MESSAGES.SOMETHING_WRONG_PLEASE_TRY_AGAIN'),
                });
            }
        } finally {
            setIsSubmittingDocument(false);
        }
    };

    const handlePrimaryAction = () => {
        if (activeStep === 0) {
            if (validateStepOne()) {
                setActiveStep(1);
            }
            return;
        }
        handleSaveDocument();
    };

    const handleBack = () => {
        setActiveStep((prev) => Math.max(prev - 1, 0));
    };

    const handleDeleteDocument = async (id) => {
        if (!canDeleteDocuments) return;
        try {
            const confirmation = await Swal.fire({
                title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
                text: t('ALERT_MESSAGES.DOCUMENT_PERMANENTLY_DELETED'),
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText:  t('GENERAL.YES_DELETE_IT'),
            });

            if (!confirmation.isConfirmed) return;

            await deleteDocument(id);

            setDocuments((prevDocuments) => prevDocuments.filter((document) => document.id !== id));
            fetchCategories();
            if (viewDoc?.id === id) {
                handleCloseViewDocument();
            }

            Swal.fire({
                icon: "success",
                title: t('ALERT_MESSAGES.DELETED'),
                text: t('ALERT_MESSAGES.DOCUMENT_DELETED_SUCCESSFULLY'),
            });
        } catch (err) {
            console.error("Error deleting document:", err);
            Swal.fire({
                icon: "error",
                title: t('ALERT_MESSAGES.ERROR'),
                text: t('ALERT_MESSAGES.COULD_NOT_DELETE_DOCUMENT'),
            });
        }
    };

    const handleCloseModal = () => {
        resetFormState();
        setIsEditing(false);
        setEditDocId(null);
        setOpenDialog(false);
    };

    const handleEditOpen = async (row) => {
        setIsEditing(true);
        setEditDocId(row.id);
        setActiveStep(0);
        setFormErrors([]);
        setError({});
        const scope = row.visibility_scope || 'all';
        const targets = Array.isArray(row.visibility_targets) ? row.visibility_targets : [];
        const customerSelections = targets
            .filter((t) => t?.all_sites && t?.cus_id)
            .map((t) => ({
                cus_id: t.cus_id,
                customer_name: t.customer_name,
                customer_number: t.customer_number,
            }));
        const siteSelections = targets
            .filter((t) => !t?.all_sites && t?.site_id && t?.cus_id)
            .map((t) => ({
                site_id: t.site_id,
                cus_id: t.cus_id,
                customer_site_name: t.customer_site_name,
                customer_site_number: t.customer_site_number,
                parent_customer_name: t.customer_name,
            }));
        setSelectedAudienceCustomers(customerSelections);
        setSelectedAudienceSites(siteSelections);
        setVisibilityScope(scope);
        const matchingSiteNumber =
            scopedSiteData.find((site) => Number(site.site_id) === Number(row.site_id))?.customer_site_number ||
            selectedSite ||
            "";
        setFormSiteNumber(matchingSiteNumber);

        try {
            if (row.assigned_machines && Array.isArray(row.assigned_machines) && row.assigned_machines.length > 0) {
                const resp = await getMachinesByIds({ machines: row.assigned_machines });
                if (resp?.status) setSelectedMachines(resp.data || []);
                else setSelectedMachines([]);
            } else {
                setSelectedMachines([]);
            }
        } catch (e) {
            setSelectedMachines([]);
        }

        const docCategories = Array.isArray(row.categories) ? row.categories : [];
        setSelectedDocCategories(
            docCategories.map((cat) => ({
                id: cat.category_id || cat.id || null,
                label: cat.category_name || cat.label || "",
                isNew: false,
            }))
        );
        setNewDoc({
            name: row.name,
            description: row.description || '',
            attachment_type: row.attachment_type || 'none',
            url: row.attachment_type === 'url' ? (row.attachment || '') : '',
            files: [],
            status: row.visibility_status || 'private',
        });
        setOpenDialog(true);
    };

    if (!canManageDocuments) {
        return <NotAuthorized />;
    }

    const modalTitle = isEditing
        ? t('UI_TEXT.EDIT_DOCUMENT', { defaultValue: 'Edit Document' })
        : t('UI_TEXT.ADD_NEW_DOCUMENT', { defaultValue: 'Add New Document' });

    return (
        <div>
            <Tabs value={tabIndex} onChange={(event, value) => setTabIndex(value)} sx={{ mb: 2 }}>
                <Tab label={t('LABELS.DOCUMENTS') || 'Documents'} value={0} />
                <Tab label={t('LABELS.DOCUMENT_CATEGORIES') || 'Categories'} value={1} />
            </Tabs>

            {tabIndex === 0 && (
                documentsLoading ? (
                    <DatagridLoading
                        actionButtons={customButtons.length}
                        columns={6}
                        rows={6}
                    />
                ) : (
                    <Datagrid
                        data={documents}
                        columns={columns}
                        rowIdField="id"
                        customButtons={customButtons}
                        checkboxSelection={true}
                        onSelect={(ids) => setSelectedRows(ids)}
                        rowClick
                        onRowClick={(params) => handleViewDocument(params.row)}
                            hideFilterFromColumnMenu
                            lockActiveColumnInManageColumns
                            preventHidingLastColumn
                    />
                )
            )}

            {tabIndex === 1 && (
                categoriesLoading ? (
                    <DatagridLoading
                        actionButtons={customButtonsCategory.length}
                        columns={4}
                        rows={6}
                    />
                ) : (
                    <Datagrid
                        data={categoryGridRows}
                        columns={categoryColumns}
                        rowIdField="id"
                        checkboxSelection={false}
                        rowClick
                        customButtons={customButtonsCategory}
                        onRowClick={(params) => handleOpenCategoryDetails(params.row)}
                            hideFilterFromColumnMenu
                            lockActiveColumnInManageColumns
                            preventHidingLastColumn
                    />
                )

            )}

            <ModalWrapper
                open={openDialog}
                setOpen={setOpenDialog}
                onClose={handleCloseModal}
                title={modalTitle}
                content={
                    <Box sx={{ overflowY: "auto", p: 1 }}>
                        {formErrors.length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                                <Alert severity="error">
                                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Please fix the following:</div>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {formErrors.map((msg, idx) => (<li key={idx}>{msg}</li>))}
                                    </ul>
                                </Alert>
                            </Box>
                        )}
                        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        {activeStep === 0 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={categorySelectOptions}
                                    value={selectedDocCategories}
                                    onChange={handleCategorySelection}
                                    getOptionLabel={(option) => option.label || ""}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                variant="outlined"
                                                label={option.label}
                                                {...getTagProps({ index })}
                                                key={`${option.label}-${index}`}
                                            />
                                        ))
                                    }
                                    loading={false}
                                    loadingText={t('UI_TEXT.LOADING') || 'Loading...'}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('LABELS.CATEGORY')}
                                            size="small"
                                            error={!!error.categories}
                                            helperText={error.categories}
                                        />
                                    )}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    <b>Note:</b> {categoryCreationHint}
                                </Typography>

                                <TextField
                                    label={t('TABLE_HEADERS.DOCUMENT_NAME')}
                                    value={newDoc.name}
                                    onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })}
                                    size="small"
                                    fullWidth
                                    error={!!error.name}
                                    helperText={error.name}
                                />

                                <RichTextEditor
                                    value={newDoc.description}
                                    onChange={(val) => setNewDoc({ ...newDoc, description: val?.html || '' })}
                                    height={250}
                                    placeholder={t('LABELS.DOCUMENT_DESCRIPTION')}
                                />

                                <FormControl fullWidth size="small">
                                    <InputLabel id="document-status-label">{t('LABELS.DOCUMENT_STATUS') || 'Document Status'}</InputLabel>
                                    <Select
                                        labelId="document-status-label"
                                        label={t('LABELS.DOCUMENT_STATUS') || 'Document Status'}
                                        value={newDoc.status}
                                        onChange={(e) => setNewDoc({ ...newDoc, status: e.target.value })}
                                    >
                                        <MenuItem value="private">{t('LABELS.PRIVATE') || 'Private'}</MenuItem>
                                        <MenuItem value="public">{t('LABELS.PUBLIC') || 'Public'}</MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth size="small">
                                    <InputLabel id="attachment-type-label">Attachment Type</InputLabel>
                                    <Select
                                        labelId="attachment-type-label"
                                        label="Attachment Type"
                                        value={newDoc.attachment_type}
                                        onChange={(e) => setNewDoc({ ...newDoc, attachment_type: e.target.value })}
                                    >
                                        <MenuItem value="none">No Attachment</MenuItem>
                                        <MenuItem value="file">File Attachment</MenuItem>
                                        <MenuItem value="url">URL Attachment</MenuItem>
                                    </Select>
                                </FormControl>



                                {newDoc.attachment_type === 'file' && (
                                    <Box>
                                         <Alert severity="info" sx={{ mb: 1 }}>
            Only <b>PDF</b>, <b>Word</b>, and <b>Excel</b> files can be uploaded (Max 25 MB).
        </Alert>
                                        {isEditing && (
                                            <Box sx={{ mb: 1 }}>
                                                <div style={{ fontSize: 12, marginBottom: 6, color: '#666' }}>Current Attachment:</div>
                                                {(() => {
                                                    const current = documents.find(d => d.id === editDocId) || {};
                                                    const link = current.attachment || (current.name ? `Documents/${current.name}` : '');
                                                    const typeLabel = (() => {
                                                        const n = current.name || '';
                                                        const idx = n.lastIndexOf('.');
                                                        return idx > -1 ? n.slice(idx + 1).toUpperCase() : 'FILE';
                                                    })();
                                                    return (
                                                        <DocCard
                                                            title={current.name || 'Open current file'}
                                                            href={link ? `${SERVER_BASE_URL}/${link}` : undefined}
                                                            subtitle={t('BUTTON.SAVED') || 'Saved'}
                                                            chipLabel={typeLabel}
                                                            imageProps={{ filename: current.name, path: current.attachment }}
                                                        />
                                                    );
                                                })()}
                                                <div style={{ fontSize: 12, marginTop: 8, color: '#666' }}>Select a new file below to replace (optional):</div>
                                            </Box>
                                        )}
                                       <FileDropZone
                                            key={fileDropzoneKey}  
                                            maxFiles={1}
                                            setParentData={(updater) => {
                                                setNewDoc((prev) => {
                                                    const updated =
                                                        typeof updater === "function" ? updater(prev) : updater;

                                                    const file = updated.files?.[0];
                                                    if (file && file.size > MAX_FILE_SIZE) {
                                                        setError((prevErr) => ({
                                                            ...prevErr,
                                                            file: "File size must not exceed 25 MB",
                                                        }));
                                                        setFileDropzoneKey((k) => k + 1);
                                                        return {
                                                            ...prev,
                                                            files: [],
                                                        };
                                                    }
                                                    setError((prevErr) => ({
                                                        ...prevErr,
                                                        file: undefined,
                                                    }));
                                                    return {
                                                        ...prev,
                                                        files: updated.files,
                                                    };
                                                });
                                            }}
                                        />



                                        {error.file && <FormHelperText error sx={{ mt: 0.5 }}>{error.file}</FormHelperText>}
                                    </Box>
                                )}

                                {newDoc.attachment_type === 'url' && (
                                    <TextField
                                        label={t('LABELS.DOCUMENT_URL')}
                                        value={newDoc.url}
                                        onChange={(e) => setNewDoc({ ...newDoc, url: e.target.value })}
                                        size="small"
                                        fullWidth
                                        error={!!error.url}
                                        helperText={error.url}
                                    />
                                )}


                            </Box>
                        )}

                        {activeStep === 1 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
                                <Autocomplete
                                    size="small"
                                    multiple
                                    options={machines}
                                    disablePortal
                                    filterOptions={(x) => x}
                                    getOptionLabel={(machine) => `${machine.machine_number} - ${machine.machine_name}`}
                                    isOptionEqualToValue={(option, value) => option.machine_id === value.machine_id}
                                    value={selectedMachines}
                                    onChange={(event, newValue) => setSelectedMachines(newValue)}
                                    onInputChange={(e, value) => {
                                        setMachineSearch(value);
                                        clearTimeout(window.__md_search_timer__);
                                        window.__md_search_timer__ = setTimeout(() => {
                                            fetchMachineResults(1, value || "");
                                        }, 300);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('LABELS.ASSIGNED_TO_MACHINES')}
                                            variant="outlined"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {machinesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    ListboxProps={{
                                        onScroll: (e) => {
                                            const listboxNode = e.currentTarget;
                                            if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10) {
                                                if (!machinesLoading && machineHasMore) {
                                                    fetchMachineResults(machinePage + 1, machineSearch);
                                                }
                                            }
                                        },
                                        style: { maxHeight: 300, overflow: 'auto' },
                                    }}
                                />
                                {error.machines && <FormHelperText error>{error.machines}</FormHelperText>}

                                <Box>
                                    <Typography variant="subtitle2">Customers with selected machines</Typography>
                                    {audienceLoading ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                            <CircularProgress size={16} />
                                            <Typography variant="body2" color="text.secondary">Loading customers...</Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                            {audienceOptions.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary">Select machines to view matching customers.</Typography>
                                            ) : (
                                                audienceOptions.map((cust) => (
                                                    <Chip key={cust.cus_id} size="small" label={`${cust.customer_name} (${cust.sites.length} site${cust.sites.length === 1 ? '' : 's'})`} />
                                                ))
                                            )}
                                        </Box>
                                    )}
                                </Box>

                                <FormControl>
                                    <RadioGroup
                                        row
                                        value={visibilityScope}
                                        onChange={(e) => setVisibilityScope(e.target.value)}
                                    >
                                        <FormControlLabel
                                            value="all"
                                            control={<Radio />}
                                            label={visibilityAllLabel}
                                        />
                                        <FormControlLabel
                                            value="custom"
                                            control={<Radio />}
                                            label={customVisibilityLabel}
                                        />
                                    </RadioGroup>
                                </FormControl>

                                {!hasMachines && visibilityScope === "all" && (
                                    <Alert severity="info">
                                        {allCustomersNote}
                                    </Alert>
                                )}

                                {hasMachines && visibilityScope === "all" && (
                                    <Alert severity="info">
                                        {matchingCustomersNote}
                                    </Alert>
                                )}

                                {visibilityScope === "custom" && (
                                    audienceLoading && hasMachines ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CircularProgress size={16} />
                                            <Typography variant="body2" color="text.secondary">
                                                Loading customer locations...
                                            </Typography>
                                        </Box>
                                    ) : currentLocationRows.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            {hasMachines
                                                ? 'No customer locations found for the selected machines.'
                                                : 'No customer locations available.'}
                                        </Typography>
                                    ) : (
                                        <Datagrid
                                            data={currentLocationRows}
                                            columns={locationColumns}
                                            rowIdField="site_id"
                                            checkboxSelection
                                            showSearchBar={true}
                                            keepNonExistentSelected={true}
                                            disableSelectAll
                                            rowSelectionModel={locationSelectionModel}
                                            onSelect={handleLocationSelectionChange}
                                            autoHeight={false}
                                            gridHeight={480}
                                                    hideFilterFromColumnMenu
                                                    lockActiveColumnInManageColumns
                                                    preventHidingLastColumn
                                        />
                                    )
                                )}
                                {visibilityScope === "custom" && error.visibility && <FormHelperText error>{error.visibility}</FormHelperText>}
                            </Box>
                        )}
                    </Box>
                }
                actions={
                    <>
                        <Box sx={{ display: 'flex', justifyContent: activeStep > 0 ? 'space-between' : 'flex-end', width: '100%', alignItems: 'center' }}>
                            {activeStep > 0 && (
                                <Button onClick={handleBack} color="secondary" sx={{ mr: 1 }}>
                                    {t('BUTTON.BACK') || 'Back'}
                                </Button>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', alignItems: 'center', columnGap: 1.5 }}>

                                <Button variant="outlined" onClick={handleCloseModal} color="primary">
                                    {t('BUTTON.CANCEL')}
                                </Button>

                                <Button
                                    onClick={handlePrimaryAction}
                                    color="primary"
                                    variant="contained"
                                    disabled={isSubmittingDocument}
                                    startIcon={isSubmittingDocument ? <CircularProgress size={16} /> : undefined}
                                >
                                    {activeStep === steps.length - 1
                                        ? (isEditing ? (t('BUTTON.UPDATE') || 'Update') : (t('BUTTON.ADD_DOCUMENT') || 'Add Document'))
                                        : (t('BUTTON.NEXT') || 'Next')}
                                </Button>
                            </Box>
                        </Box>
                    </>
                }
            />

            <CategoryViewModal
                open={categoryDetailsModal.open}
                category={categoryDetailsModal.category}
                documents={categoryDetailsModal.documents}
                onClose={handleCloseCategoryDetails}
                onViewDocument={handleViewDocument}
                onRemoveDocument={(doc) =>
                    handleRemoveDocumentFromCategory(categoryDetailsModal.category?.id, doc.id)
                }
                onAddDocument={handleAddDocumentFromCategory}
                onAssignDocuments={handleOpenAssignModal}
                onDeleteCategory={handleDeleteCategoryRequest}
            />

            <DeleteCategoryModal
                open={deleteCategoryModalState.open}
                category={deleteCategoryModalState.category}
                categories={scopedCategories}
                exclusiveDocuments={exclusiveDocsForDelete}
                categoryDocuments={categoryDocumentsForDelete}
                defaultCategory={defaultCategory}
                onClose={handleCloseDeleteCategoryModal}
                onConfirm={handleConfirmDeleteCategory}
            />

            <AddCategoryModal
                open={addCategoryModalOpen}
                onClose={() => setAddCategoryModalOpen(false)}
                onSubmit={handleCreateCategory}
            />

            <EditCategoryModal
                open={editCategoryModal.open}
                category={editCategoryModal.category}
                onClose={() => setEditCategoryModal({ open: false, category: null })}
                onSubmit={handleEditCategorySubmit}
            />

            <ModalWrapper
                open={assignModal.open}
                setOpen={(open) => {
                    if (!open) handleAssignModalClose();
                }}
                onClose={handleAssignModalClose}
                title={`${t('LABELS.CATEGORY') || 'Category'}: ${assignModal.category?.category_name || ''}`}
                content={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1, width: '100%' }}>
                        <Autocomplete
                            multiple
                            options={documents}
                            getOptionLabel={(option) => option.name || option.document_name || ''}
                            value={assignSelectedDocs}
                            onChange={handleAssignDocumentsChange}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip {...getTagProps({ index })} key={`${option.id}-${index}`} label={option.name || option.document_name || ''} />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField {...params} label={t('LABELS.DOCUMENTS') || 'Documents'} size="small" />
                            )}
                        />
                    </Box>
                }
                actions={
                    <>
                        <Button onClick={handleAssignModalClose}>{t('BUTTON.CANCEL')}</Button>
                        <Button variant="contained" onClick={handleAssignDocumentsSave}>{t('BUTTON.SAVE') || 'Save'}</Button>
                    </>
                }
            />

            <DocumentViewModal
                open={viewModalOpen}
                setOpen={setViewModalOpen}
                document={viewDoc}
                onClose={handleCloseViewDocument}
                onEdit={
                    canEditDocuments && viewDoc
                        ? () => {
                            handleCloseViewDocument();
                            handleEditOpen(viewDoc);
                        }
                        : undefined
                }
                onDelete={canDeleteDocuments && viewDoc ? () => handleDeleteDocument(viewDoc.id) : undefined}
            />
        </div>
    );
};

export default ManageDocuments;
